import { useState } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import type {
  DropzoneFile,
  DatabaseFileLabel,
  ClassificationResult,
} from "../types";

interface UseFileSavingDeps {
  classificationResults: Record<string, ClassificationResult>;
  fileLabels: DatabaseFileLabel[];
  getEffectiveUnitId: (file: DropzoneFile) => string | null;
  getEffectiveBuildingAttributeId: (file: DropzoneFile) => number | null;
  prepareDdAnswersForSave: (fileId: string) => any;
}

interface UseFileSavingReturn {
  savingFile: boolean;
  savedFileIds: Set<string>;
  setSavedFileIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  labelPickerOpen: boolean;
  setLabelPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFileForLabeling: DropzoneFile | null;
  setSelectedFileForLabeling: React.Dispatch<React.SetStateAction<DropzoneFile | null>>;
  handleLabelSelect: (selectedLabel: DatabaseFileLabel) => Promise<void>;
  handleDirectLabelSave: (file: DropzoneFile, suggestedLabelText: string) => Promise<void>;
  getEnhancedFileLabels: () => DatabaseFileLabel[];
}

export function useFileSaving(
  buildingId: string,
  session: any,
  user: any,
  deps: UseFileSavingDeps
): UseFileSavingReturn {
  const [savingFile, setSavingFile] = useState(false);
  const [savedFileIds, setSavedFileIds] = useState<Set<string>>(new Set());
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [selectedFileForLabeling, setSelectedFileForLabeling] = useState<DropzoneFile | null>(null);

  const handleLabelSelect = async (selectedLabel: DatabaseFileLabel) => {
    if (!selectedFileForLabeling || !user) return;

    const file = selectedFileForLabeling;
    const classification = deps.classificationResults[file.id];
    const ddAnswers = deps.prepareDdAnswersForSave(file.id);

    setSavingFile(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-save`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileId: file.id,
            fileName: file.name,
            mimeType: file.type,
            fileSize: file.size,
            sharePointUrl: file.sharePointUrl,
            labelId: selectedLabel.id,
            unitId: deps.getEffectiveUnitId(file),
            buildingAttributeId: deps.getEffectiveBuildingAttributeId(file),
            documentSummary: classification?.documentSummary || null,
            createdByUserId: user.id,
            dueDiligenceAnswers: ddAnswers,
            expirationDate: classification?.extractedStandardFields?.expirationDate?.value || null,
            documentDate: classification?.extractedStandardFields?.documentDate?.value || null,
            signStatus: classification?.extractedStandardFields?.signStatus?.value || null,
          }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij opslaan (${response.status})`
        );
      }

      setSavedFileIds((prev) => new Set([...prev, file.id]));
      toast.success(
        `Bestand "${file.name}" opgeslagen met label "${selectedLabel.label}"`
      );

      setLabelPickerOpen(false);
      setSelectedFileForLabeling(null);
    } catch (err: any) {
      console.error("Error saving file:", err);
      Sentry.captureException(err, {
        extra: { buildingId, fileId: file.id, context: "DropzoneTab save" },
      });
      toast.error(err.message || "Fout bij opslaan van bestand");
    } finally {
      setSavingFile(false);
    }
  };

  const handleDirectLabelSave = async (
    file: DropzoneFile,
    suggestedLabelText: string
  ) => {
    if (!user) return;

    console.log(
      `[DropzoneTab] handleDirectLabelSave called with suggestedLabelText: "${suggestedLabelText}"`
    );

    const normalizedSuggestion = suggestedLabelText.trim().toLowerCase();
    const matchingLabel = deps.fileLabels.find(
      (l) => l.label.trim().toLowerCase() === normalizedSuggestion
    );

    if (!matchingLabel) {
      toast.error(`Label "${suggestedLabelText}" niet gevonden in database`);
      Sentry.captureMessage("AI suggested label not found in database", {
        level: "warning",
        extra: {
          suggestedLabelText,
          normalizedSuggestion,
          availableLabels: deps.fileLabels.map((l) => l.label),
        },
      });
      return;
    }

    const classification = deps.classificationResults[file.id];
    const ddAnswers = deps.prepareDdAnswersForSave(file.id);

    setSavingFile(true);

    const requestBody = {
      fileId: file.id,
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      sharePointUrl: file.sharePointUrl,
      labelId: matchingLabel.id,
      unitId: deps.getEffectiveUnitId(file),
      buildingAttributeId: deps.getEffectiveBuildingAttributeId(file),
      documentSummary: classification?.documentSummary || null,
      createdByUserId: user.id,
      dueDiligenceAnswers: ddAnswers,
      expirationDate: classification?.extractedStandardFields?.expirationDate?.value || null,
      documentDate: classification?.extractedStandardFields?.documentDate?.value || null,
      signStatus: classification?.extractedStandardFields?.signStatus?.value || null,
    };

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-save`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij opslaan (${response.status})`
        );
      }

      setSavedFileIds((prev) => new Set([...prev, file.id]));
      toast.success(
        `Bestand "${file.name}" opgeslagen met label "${matchingLabel.label}"`
      );
    } catch (err: any) {
      console.error("Error saving file:", err);
      Sentry.captureException(err, {
        extra: { buildingId, fileId: file.id, context: "DropzoneTab direct save" },
      });
      toast.error(err.message || "Fout bij opslaan van bestand");
    } finally {
      setSavingFile(false);
    }
  };

  const getEnhancedFileLabels = (): DatabaseFileLabel[] => {
    if (!selectedFileForLabeling) return deps.fileLabels;

    const classification = deps.classificationResults[selectedFileForLabeling.id];
    if (!classification?.suggestedLabels?.length) return deps.fileLabels;

    const suggestedMap = new Map(
      classification.suggestedLabels.map((s) => [s.label.toLowerCase(), s])
    );

    return [...deps.fileLabels].sort((a, b) => {
      const aSuggestion = suggestedMap.get(a.label.toLowerCase());
      const bSuggestion = suggestedMap.get(b.label.toLowerCase());

      if (aSuggestion && bSuggestion) {
        return bSuggestion.confidence - aSuggestion.confidence;
      }
      if (aSuggestion) return -1;
      if (bSuggestion) return 1;
      return a.label.localeCompare(b.label);
    });
  };

  return {
    savingFile,
    savedFileIds,
    setSavedFileIds,
    labelPickerOpen,
    setLabelPickerOpen,
    selectedFileForLabeling,
    setSelectedFileForLabeling,
    handleLabelSelect,
    handleDirectLabelSave,
    getEnhancedFileLabels,
  };
}
